import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import ProductCard from '../components/ProductCard';
import { categoryNames } from '../data/products';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const activeCategory = searchParams.get('category') || '';
  const currentSearch = searchParams.get('search') || '';

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = {};
        if (activeCategory) params.category = activeCategory;
        if (currentSearch) params.search = currentSearch;

        const { data } = await axios.get(`${API}/products`, { params });
        setProducts(data);
      } catch (err) {
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [activeCategory, currentSearch]);

  const handleSearch = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (searchQuery.trim()) {
      newParams.set('search', searchQuery.trim());
    } else {
      newParams.delete('search');
    }
    setSearchParams(newParams);
  };

  const setCategory = (cat) => {
    const newParams = new URLSearchParams(searchParams);
    if (cat) {
      newParams.set('category', cat);
    } else {
      newParams.delete('category');
    }
    newParams.delete('search');
    setSearchQuery('');
    setSearchParams(newParams);
  };

  return (
    <div className="animate-fade-in" data-testid="products-page">
      <div className="bg-[#eef6f2] py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#46371c]">
            {activeCategory ? categoryNames[activeCategory] || 'Productos' : 'Todos los Productos'}
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-2 flex-wrap mb-8" data-testid="category-filters">
          <Filter className="w-4 h-4 text-[#75664d]" />
          <button
            onClick={() => setCategory('')}
            className={`px-4 py-1.5 rounded-full font-body text-sm transition-colors ${!activeCategory ? 'bg-[#46371c] text-[#fcfbf9]' : 'bg-[#f4f1ea] text-[#46371c] hover:bg-[#e5dfd3]'}`}
            data-testid="filter-all"
          >Todos</button>
          {Object.entries(categoryNames).map(([id, name]) => (
            <button
              key={id}
              onClick={() => setCategory(id)}
              className={`px-4 py-1.5 rounded-full font-body text-sm transition-colors ${activeCategory === id ? 'bg-[#46371c] text-[#fcfbf9]' : 'bg-[#f4f1ea] text-[#46371c] hover:bg-[#e5dfd3]'}`}
              data-testid={`filter-${id}`}
            >{name}</button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#a0d5ba] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <p className="font-body text-lg text-[#75664d]">No se encontraron productos</p>
            <Button onClick={() => { setCategory(''); setSearchQuery(''); }} className="mt-4 bg-[#46371c] text-[#fcfbf9] rounded-full" data-testid="clear-filters-btn">
              Ver todos
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" data-testid="products-grid">
            {products.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </div>
    </div>
  );
}
